import isEqual from 'lodash/isEqual';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';

import {
  Button,
  ButtonGroup,
  Dialog,
  RailModalCloseButton,
  RailModalContent,
  RailModalDivider,
  RailModalHeader,
  useDisclosure,
} from '@revelio/ui';

import { CardListSelect, type ICardListSelectProps } from './card-list-select';
import styles from './card-list-sub-menu.module.css';

export interface CardGridProps {
  spacing?: number | string;
  columns?: number;
  gridAutoFlow?: CSSProperties['gridAutoFlow'];
}

export const cardGridStyle = ({
  spacing,
  columns,
  gridAutoFlow,
}: CardGridProps) =>
  ({
    '--card-grid-gap':
      typeof spacing === 'number' || /^\d+(\.\d+)?$/.test(spacing ?? '')
        ? `${Number(spacing) * 0.25}rem`
        : spacing,
    '--card-grid-columns': columns,
    '--card-grid-auto-flow': gridAutoFlow,
  }) as CSSProperties;

export function isColumnCardListSubmMenu<CardType>(
  column: CardListSubMenuProps<CardType> | ICardListSelectProps<CardType>
): column is CardListSubMenuProps<CardType> {
  return (column as CardListSubMenuProps<CardType>).menuItems !== undefined;
}

export type OnCloseSubMenuProp<CardType> = (
  previouslyCheckedColumns: ICardListSelectProps<CardType>[],
  allSubMenuColumns: ICardListSelectProps<CardType>[]
) => void;
export interface CardListSubMenuProps<CardType>
  extends ICardListSelectProps<string> {
  gridProps?: CardGridProps;
  menuItems: (ICardListSelectProps<CardType> & {
    excludeFromFileRowMax?: boolean;
  })[];
  onCloseSubMenu?: OnCloseSubMenuProp<CardType>;
  menuHeaderContent?: ReactNode;
}
function CardListSubMenu<CardType extends string>({
  menuItems,
  gridProps,
  isRequired,
  onCloseSubMenu,
  menuHeaderContent,
  ...menuButtonProps
}: CardListSubMenuProps<CardType>) {
  const checkedSubItems = menuItems.filter((col) => col.isChecked);
  const hasCheckSubItems = !!checkedSubItems.length;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [checkedColumnsBeforeEditing, setCheckedColumnsBeforeEditing] =
    useState(checkedSubItems);

  useEffect(() => {
    // for the case of other code changing checked items, e.g. "reset to default" cta.
    // when it is re-opened and cancelled we want the latest checked items (according to state), not the ones before they were edited by the other code
    if (!isOpen && !isEqual(checkedColumnsBeforeEditing, checkedSubItems)) {
      setCheckedColumnsBeforeEditing(checkedSubItems);
    }
  }, [isOpen, checkedSubItems, checkedColumnsBeforeEditing]);

  const handleCloseSubMenuWithoutSelection = () => {
    if (onCloseSubMenu) {
      onCloseSubMenu(checkedColumnsBeforeEditing, checkedSubItems);
    }
    onClose();
  };
  const modalBody = (
    <div className={styles.body}>
      {menuHeaderContent ? (
        <div className={styles.headerContent}>{menuHeaderContent}</div>
      ) : null}
      <div
        className={styles.grid}
        style={cardGridStyle({
          spacing: 4,
          columns: menuItems.length >= 4 ? 2 : 1,
          gridAutoFlow: 'row',
          ...gridProps,
        })}
      >
        {menuItems.map((item) => (
          <CardListSelect<CardType> key={item.id} {...item} />
        ))}
      </div>
    </div>
  );

  const modalFooter = (
    <div className={styles.footer}>
      <ButtonGroup size="sm" style={{ gap: 'var(--ui-spacing-4)' }}>
        <Button
          variant="link"
          className={styles.cancelButton}
          onClick={handleCloseSubMenuWithoutSelection}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => {
            setCheckedColumnsBeforeEditing(checkedSubItems);
            onClose();
          }}
        >
          Select
        </Button>
      </ButtonGroup>
    </div>
  );

  return (
    <>
      <CardListSelect<string>
        {...menuButtonProps}
        label={`${menuButtonProps.label}${isRequired ? '*' : ''} ${
          checkedSubItems.length ? `(${checkedSubItems.length} sel)` : ''
        }`}
        isChecked={hasCheckSubItems}
        onChange={onOpen}
      />
      <Dialog
        open={isOpen}
        onClose={handleCloseSubMenuWithoutSelection}
        size="lg"
        closeButton={false}
        className={styles.dialog}
        bodyClassName={styles.dialogBody}
        aria-label={menuButtonProps.label}
      >
        <RailModalContent>
          <RailModalHeader>{menuButtonProps.label}</RailModalHeader>
          <RailModalCloseButton onClick={handleCloseSubMenuWithoutSelection} />
          <RailModalDivider />
          {modalBody}
          {modalFooter}
        </RailModalContent>
      </Dialog>
    </>
  );
}

export default CardListSubMenu;
